import {
  getCategoryAxisColorsForChart,
  appendAlphaIfHex,
  sortReportGroupKeys,
} from '../chartPicklistColors';
import { isChartGrandTotalCategory } from '../../matrix/matrixChartData';
import { getCalculatedReturnType } from '../../../../utils/reportCurrencyAggregate';

const DEFAULT_FIELD_COLORS = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#4BC0C0',
  '#9966FF',
];

export const parseSummarizedNumericValue = (rawValue) => {
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) return rawValue;
  if (typeof rawValue === 'string') {
    if (rawValue === 'N/A' || rawValue.trim() === '') return 0;
    let cleanValue = rawValue.replace(/[₹$,\s]/g, '').trim();
    if (cleanValue.endsWith('%')) cleanValue = cleanValue.replace('%', '');
    return parseFloat(cleanValue) || 0;
  }
  return 0;
};

export const calculateRecordCountForGroup = (groupValue, groupField, previewData) => {
  if (!previewData || previewData.length === 0 || !groupField) return 0;

  return previewData.filter((row) => {
    const tableName = groupField.object_name || 'main';
    const aliasedFieldName = `${tableName}_${groupField.field_name}`;
    const fieldData = row[aliasedFieldName] || row[groupField.field_name];

    let rowGroupValue = 'N/A';
    if (fieldData) {
      if (typeof fieldData === 'object' && fieldData !== null) {
        rowGroupValue = fieldData.displayValue || fieldData.value || 'N/A';
      } else {
        rowGroupValue = fieldData;
      }
    }
    return rowGroupValue === groupValue;
  }).length;
};

/**
 * Domain-level chart series from summarizedData (Chart.js-agnostic).
 * @returns {null|{ labels, series, groupFieldInfo, selectedCategoryField, selectedValueFields, xAxisLabel, yAxisLabel }}
 */
export function resolveSummarizedFieldMeta(
  valueFieldName,
  metadata,
  selectedFields = [],
  summarizedFields = {}
) {
  if (!valueFieldName || valueFieldName === 'record_count') return null;
  const fromMeta = metadata?.summarized_fields?.[valueFieldName];
  if (fromMeta) return fromMeta;

  const field = (selectedFields || []).find(
    (f) => f?.field_name === valueFieldName
  );
  if (!field) return null;

  const sumCfg =
    summarizedFields[field.id] ||
    summarizedFields[field.field_config_id] ||
    summarizedFields[field.field_id];
  const aggregation = sumCfg?.aggregation;
  if (!aggregation || String(aggregation).toLowerCase() === 'none') {
    return null;
  }

  return {
    field_name: field.field_name,
    field_label: field.field_label,
    data_type: field.data_type,
    object_name: field.object_name,
    aggregation,
    calculatedReturnType: getCalculatedReturnType(field),
  };
}

export function extractChartSeries({
  chart,
  summarizedData,
  previewData = [],
  selectedFields = [],
  summarizedFields = {},
}) {
  if (!summarizedData || Object.keys(summarizedData).length === 0) return null;

  const metadata = summarizedData._metadata;
  if (!metadata?.group_fields || !metadata?.summarized_fields) return null;

  const selectedCategoryField =
    chart.xAxisField || Object.keys(metadata.group_fields)[0];
  const selectedValueFields =
    chart.yAxisFields?.length > 0
      ? chart.yAxisFields
      : chart.yAxisField
        ? [chart.yAxisField]
        : [Object.keys(metadata.summarized_fields)[0]].filter(Boolean);

  if (!selectedCategoryField || !selectedValueFields?.length) return null;

  const groupData = summarizedData[selectedCategoryField];
  if (!groupData) return null;

  const groupFieldInfo = metadata.group_fields[selectedCategoryField];
  const labels = sortReportGroupKeys(
    Object.keys(groupData).filter((k) => !isChartGrandTotalCategory(k)),
    groupFieldInfo,
    null
  );
  const ct = String(chart.chartType || chart.chart_type || '').toLowerCase();

  const series = [];

  selectedValueFields.forEach((valueFieldName, index) => {
    let fieldInfo;
    let isVirtualRecordCount = false;

    if (valueFieldName === 'record_count') {
      fieldInfo = {
        field_label: 'Record Count',
        field_name: 'record_count',
        aggregation: 'count',
        data_type: 'number',
        is_virtual: true,
      };
      isVirtualRecordCount = true;
    } else {
      fieldInfo = resolveSummarizedFieldMeta(
        valueFieldName,
        metadata,
        selectedFields,
        summarizedFields
      );
      if (!fieldInfo) return;
    }

    const fieldColor =
      chart.fieldColors?.[valueFieldName] ||
      DEFAULT_FIELD_COLORS[index % DEFAULT_FIELD_COLORS.length];

    const perCategoryColors = getCategoryAxisColorsForChart(
      labels,
      groupFieldInfo,
      fieldColor,
      index
    );

    const data = labels.map((category) => {
      if (isVirtualRecordCount) {
        if (
          summarizedData.record_count?.[category] !== undefined
        ) {
          return summarizedData.record_count[category];
        }
        const groupField = metadata.group_fields[selectedCategoryField];
        return calculateRecordCountForGroup(category, groupField, previewData);
      }

      const groupSummary = groupData[category];
      if (groupSummary?.[valueFieldName]) {
        return parseSummarizedNumericValue(groupSummary[valueFieldName].value);
      }
      return 0;
    });

    const seriesType =
      chart.seriesTypes?.[valueFieldName] ||
      (ct === 'mixed' ? 'bar' : ct === 'line' || ct === 'area' ? 'line' : 'bar');

    const seriesLabel = fieldInfo.is_derived
      ? fieldInfo.field_label || valueFieldName
      : `${fieldInfo.field_label || valueFieldName} (${(fieldInfo.aggregation || 'SUM').toUpperCase()})`;

    const isGrowthPercent =
      Boolean(
        fieldInfo.is_derived &&
          String(fieldInfo.derived_type || '').toLowerCase() === 'period_over_period'
      ) || /growth/i.test(valueFieldName) || /growth/i.test(fieldInfo.field_label || '');

    series.push({
      fieldName: valueFieldName,
      name: seriesLabel,
      data,
      color: fieldColor,
      perCategoryColors: perCategoryColors || null,
      seriesType,
      dataType: fieldInfo.data_type,
      isGrowthPercent,
      derivedType: fieldInfo.derived_type,
    });
  });

  return {
    labels,
    series,
    groupFieldInfo,
    selectedCategoryField,
    selectedValueFields,
    xAxisLabel: chart.xAxisLabel || groupFieldInfo?.field_label || 'Categories',
    yAxisLabel: chart.yAxisLabel || 'Values',
  };
}

/**
 * Donut / funnel slice data from summarizedData.
 */
export function extractPieSliceSeries({
  chart,
  summarizedData,
  previewData = [],
  sliceFieldName,
  valueFieldName,
}) {
  if (!summarizedData || !sliceFieldName || !valueFieldName) return null;

  const sliceField = sliceFieldName;
  const valueField = valueFieldName;
  const groupData = summarizedData[sliceField];
  if (!groupData) return null;

  const metadata = summarizedData._metadata;
  const sliceFieldInfo = metadata?.group_fields?.[sliceField];

  const items = [];
  Object.keys(groupData).forEach((category) => {
    let value = 0;
    if (valueField === 'record_count') {
      if (summarizedData.record_count?.[category] !== undefined) {
        value = summarizedData.record_count[category];
      } else if (sliceFieldInfo) {
        value = calculateRecordCountForGroup(category, sliceFieldInfo, previewData);
      }
    } else {
      const groupSummary = groupData[category];
      if (groupSummary?.[valueField]) {
        value = parseSummarizedNumericValue(groupSummary[valueField].value);
      }
    }
    items.push({ name: category, value });
  });

  const colors = getCategoryAxisColorsForChart(
    items.map((i) => i.name),
    sliceFieldInfo,
    '#36A2EB',
    0
  );

  if (colors) {
    items.forEach((item, i) => {
      item.color = colors[i];
    });
  }

  return { items, sliceFieldInfo };
}

/**
 * Build Chart.js datasets from domain series (for ChartDisplay).
 */
export function seriesToChartJsDatasets(extracted, chart) {
  if (!extracted?.series?.length) return [];

  const ct = String(chart.chartType || chart.chart_type || '').toLowerCase();
  const isStacked = ct === 'stackedcolumn' || ct === 'stackedbar';

  return extracted.series.map((s) => {
    const dataset = {
      label: s.name,
      data: s.data,
      borderWidth: ct === 'line' || ct === 'area' ? 2 : 1,
      fill: ct === 'area',
      ...(isStacked ? { stack: 'stack0' } : {}),
    };

    const perCategory = s.perCategoryColors;
    const fieldColor = s.color;

    if (isStacked) {
      dataset.backgroundColor = fieldColor;
      dataset.borderColor = fieldColor;
    } else if (ct === 'line' && perCategory) {
      dataset.backgroundColor = 'rgba(0,0,0,0)';
      dataset.borderColor = fieldColor;
      dataset.pointBackgroundColor = perCategory;
      dataset.pointBorderColor = perCategory;
    } else if (ct === 'area' && perCategory) {
      dataset.backgroundColor = perCategory.map((c) => appendAlphaIfHex(c, '40'));
      dataset.borderColor = fieldColor;
      dataset.pointBackgroundColor = perCategory;
      dataset.pointBorderColor = perCategory;
    } else {
      dataset.backgroundColor = perCategory || fieldColor;
      dataset.borderColor =
        ct === 'line' || ct === 'area' ? fieldColor : perCategory || fieldColor;
    }

    return dataset;
  });
}

export default extractChartSeries;
